import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QuotePdfButton } from "@/components/admin/QuotePdfButton";
import { QuoteWorkings } from "@/components/admin/QuoteWorkings";
import { formatDateTime, money } from "@/lib/format";
import { OUTCOME_LABELS } from "./dashboardTokens";

const Fact = ({ label, children }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">{label}</p>
    {children}
  </div>
);

/** Name, company, number and what they were quoted. */
const ContactBlock = ({ lead }) => (
  <div className="rounded-lg border border-border bg-[color:var(--tt-surface-2)] p-4 text-sm">
    <p className="tt-eyebrow">Contact details</p>
    <p className="mt-2 font-semibold">{lead.full_name}</p>
    <p className="text-muted-foreground">{lead.company}</p>
    <p className="text-muted-foreground">
      {lead.email} · {lead.phone_display}
    </p>
    {lead.best_time ? (
      <p className="text-muted-foreground">Best time: {lead.best_time}</p>
    ) : null}
    <p className="mt-2 font-amount font-bold text-primary" data-testid="admin-detail-quote-ref">
      {money(lead.quote_total)}
      {lead.pricing_type === "monthly" ? "/month" : ""} · {lead.quote_ref}
    </p>
    {lead.next_step_label ? (
      <p className="mt-1 text-xs text-muted-foreground">
        Next step chosen: {lead.next_step_label}
      </p>
    ) : null}
    <QuotePdfButton quoteRef={lead.quote_ref} className="mt-3 bg-white" />
  </div>
);

/** Which rules stopped the calculator short. */
const FallbackBlock = ({ reasons }) => (
  <div
    className="rounded-lg border p-4 text-sm"
    style={{
      borderColor: "var(--tt-warn-border)",
      backgroundColor: "var(--tt-warn-bg)",
    }}
  >
    <p
      className="text-xs font-bold uppercase tracking-[0.1em]"
      style={{ color: "var(--tt-warn-text)" }}
    >
      Why it fell back
    </p>
    <ul className="mt-2 space-y-1.5" style={{ color: "#4A3B10" }}>
      {reasons.map((reason, index) => (
        <li key={reason.code || `reason-${index}`} className="text-[13px] leading-relaxed">
          {reason.message}
        </li>
      ))}
    </ul>
  </div>
);

/** Every answer they gave, grouped the way the wizard asked them. */
const AnswerGroups = ({ groups }) => (
  <div>
    <p className="tt-eyebrow">Answers given</p>
    <div className="mt-2 space-y-3">
      {groups.map((group) => (
        <div key={group.title} className="overflow-hidden rounded-lg border border-border">
          <p className="border-b border-border bg-[color:var(--tt-teal-mist)] px-3 py-2 text-xs font-bold text-[color:var(--tt-teal-deep)]">
            {group.title}
          </p>
          <dl className="divide-y divide-border">
            {group.items.map((item) => (
              <div
                key={item.key}
                className="flex items-start justify-between gap-4 px-3 py-2 text-[13px]"
              >
                <dt className="text-muted-foreground">{item.label}</dt>
                <dd className="shrink-0 font-amount font-semibold">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  </div>
);

/** The session itself, once it has arrived. */
const SessionBody = ({ detail, loading }) => {
  if (loading || detail?.loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading…
      </div>
    );
  }
  if (!detail?.session && !detail?.lead) return null;
  const session = detail.session || {};

  return (
    <div data-testid="admin-session-detail" className="space-y-5">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Fact label="Service">
          <p className="mt-0.5 font-medium" data-testid="admin-detail-service">
            {session.service_label || "Not selected"}
          </p>
        </Fact>
        <Fact label="Outcome">
          <p className="mt-0.5 font-medium">
            {OUTCOME_LABELS[session.outcome] || session.outcome}
          </p>
        </Fact>
        <Fact label="Started">
          <p className="mt-0.5">{formatDateTime(session.created_at)}</p>
        </Fact>
        <Fact label="Last activity">
          <p className="mt-0.5">{formatDateTime(session.updated_at)}</p>
        </Fact>
      </div>

      {detail.lead ? <ContactBlock lead={detail.lead} /> : null}

      {session.fallback_reasons?.length ? (
        <FallbackBlock reasons={session.fallback_reasons} />
      ) : null}

      {detail.quote?.quote?.sections?.length ? (
        <div>
          <p className="tt-eyebrow">How we got to this figure</p>
          <div className="mt-2">
            <QuoteWorkings quote={detail.quote.quote} />
          </div>
        </div>
      ) : null}

      {detail.answer_summary?.length ? (
        <AnswerGroups groups={detail.answer_summary} />
      ) : (
        <p className="text-sm text-muted-foreground">
          This session didn't get far enough to record any answers.
        </p>
      )}
    </div>
  );
};

/** Everything captured for one quote, or for an unfinished session. */
export const SessionDetailDialog = ({ detail, loading, onClose }) => {
  const quoteRef = detail?.lead?.quote_ref || detail?.quote?.quote_ref;
  return (
    <Dialog open={!!detail} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[86vh] max-w-2xl overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle data-testid="admin-detail-title">
            {quoteRef ? `Quote ${quoteRef}` : "Session detail"}
          </DialogTitle>
          <DialogDescription>
            {quoteRef
              ? "Everything captured for this quote, including the answers behind it."
              : "Everything captured for this calculator session, including answers."}
          </DialogDescription>
        </DialogHeader>

        <SessionBody detail={detail} loading={loading} />
      </DialogContent>
    </Dialog>
  );
};
